@file:Suppress(
    "RETURN_VALUE_NOT_USED",
    "RETURN_VALUE_NOT_USED_COERCION",
)

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Suppress
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import dev.xcyn.site.components.layouts.MarkdownLayout as _dev_xcyn_site_components_layouts_MarkdownLayout
import dev.xcyn.site.components.layouts.PageLayout as _dev_xcyn_site_components_layouts_PageLayout
import dev.xcyn.site.pages.AboutPage as _dev_xcyn_site_pages_AboutPage
import dev.xcyn.site.pages.HomePage as _dev_xcyn_site_pages_HomePage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "xcyn.dev"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "dev.xcyn.site.components.layouts.MarkdownLayout",
            parentLayoutId = "dev.xcyn.site.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                dev.xcyn.site.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _dev_xcyn_site_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "dev.xcyn.site.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _dev_xcyn_site_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "dev.xcyn.site.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                dev.xcyn.site.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _dev_xcyn_site_pages_HomePage()
        }
        ctx.router.register(
            "/about",
            layoutId = "dev.xcyn.site.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                dev.xcyn.site.pages.initAboutPage(ctx)
            },
        ) { pageCtx -> 
            _dev_xcyn_site_pages_AboutPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("page-content", dev.xcyn.site.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("markdown", dev.xcyn.site.components.layouts.MarkdownStyle)
        ctx.theme.registerStyle("social-platform", dev.xcyn.site.components.sections.SocialPlatformStyle)
        ctx.theme.registerStyle("social-handle", dev.xcyn.site.components.sections.SocialHandleStyle)
        ctx.theme.registerStyle("footer", dev.xcyn.site.components.sections.FooterStyle)
        ctx.theme.registerStyle("nav-header", dev.xcyn.site.components.sections.NavHeaderStyle)
        ctx.theme.registerStyle("nav-link", dev.xcyn.site.components.sections.NavLinkStyle)
        ctx.theme.registerStyle("headline-text", dev.xcyn.site.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", dev.xcyn.site.SubheadlineTextStyle)
        ctx.theme.registerVariant("-circle", dev.xcyn.site.CircleButtonVariant
        )
        ctx.theme.registerVariant("-uncolored", dev.xcyn.site.UncoloredButtonVariant
        )
        ctx.theme.registerKeyframes("side-menu-slide-in", dev.xcyn.site.components.sections.SideMenuSlideInAnim)
        dev.xcyn.site.initTheme(ctx)
        dev.xcyn.site.initSiteStyles(ctx)
        dev.xcyn.site.initColorMode(ctx)
        dev.xcyn.site.initStyles(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        dev.xcyn.site.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
